/**
 * Taking pages off a site the way deleting a page on Live does: each page
 * inside a removed one moves up to the nearest ancestor that stays.
 *
 * Unlike the live DELETE route, `ord` is not renumbered, so pages that did
 * not move keep the same content hash and versions holding them do not see
 * a conflict. Pure: the server, the editor preview and subsites share it.
 */

const parentOf = (page) => page?.parentId || page?.parentUrl || null;

/**
 * @param {Object} pages - pages keyed by id
 * @param {Iterable<string>} ids - pages to remove; ids not in `pages` are ignored
 * @returns {{ pages: Object, removed: Object, moved: Object }}
 *   removed: the records that were taken out, by id;
 *   moved: each page that moved up, as it was before the move.
 */
export function removePages(pages, ids) {
    const source = pages || {};
    const drop = new Set([...(ids || [])].filter((id) => source[id]));
    if (!drop.size) return { pages: source, removed: {}, moved: {} };

    const keptAncestor = (id) => {
        const seen = new Set();
        let current = parentOf(source[id]);
        while (current && drop.has(current) && !seen.has(current)) {
            seen.add(current);
            current = parentOf(source[current]);
        }
        return current && source[current] ? current : null;
    };

    const next = {};
    const removed = {};
    const moved = {};
    Object.entries(source).forEach(([id, page]) => {
        if (drop.has(id)) {
            removed[id] = page;
            return;
        }
        if (drop.has(parentOf(page))) {
            moved[id] = page;
            const { parentUrl: _parentUrl, ...rest } = page;
            next[id] = { ...rest, parentId: keptAncestor(id) };
            return;
        }
        next[id] = page;
    });
    return { pages: next, removed, moved };
}

export default { removePages };
